import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { absoluteUrl } from "@/lib/site";
import { BeatCard } from "@/components/beats/BeatCard";
import { beatsCatalogQuery, beatsEngagementQuery, beatsTiersQuery } from "@/lib/bkls-queries";
import {
  applyBeatFilters,
  activeFilterCount,
  facetsFor,
  sortBeats,
  SORT_OPTIONS,
  EMPTY_FILTERS,
  type BeatFilters,
  type SortKey,
} from "@/lib/bkls-marketplace";
import { getTopic } from "@/lib/taxonomy";
import { track } from "@/lib/analytics";

type Search = Partial<BeatFilters> & { sort?: SortKey };

const SORT_KEYS = SORT_OPTIONS.map((s) => s.key) as readonly string[];

function str(value: unknown, max = 80): string {
  return typeof value === "string" ? value.slice(0, max) : "";
}

const TITLE = "Beat Catalog | Search Buddha KA Beats by Genre, Mood, Key and Tempo";
const DESCRIPTION =
  "Search the full Buddha KA licensing catalog. Filter original productions by genre, mood, musical key, tempo, collection and topic, and preview every beat before you license it.";

export const Route = createFileRoute("/beats/catalog")({
  // Only non-default values are kept in the URL. Emitting empty defaults would
  // make the router rewrite the clean /beats/catalog URL into a parameterised
  // one, which duplicates the canonical document for crawlers.
  validateSearch: (search: Record<string, unknown>): Search => {
    const out: Search = {};
    const put = (key: keyof Search, value: string) => {
      if (value) (out as Record<string, unknown>)[key] = value;
    };
    put("q", str(search.q, 120));
    put("genre", str(search.genre));
    put("mood", str(search.mood));
    put("musicalKey", str(search.musicalKey));
    put("collection", str(search.collection));
    put("bpmBand", str(search.bpmBand, 16));
    put("topic", str(search.topic));
    put("tag", str(search.tag));
    if (search.instantOnly === true || search.instantOnly === "true") out.instantOnly = true;
    const sort = str(search.sort, 20);
    if (SORT_KEYS.includes(sort) && sort !== "newest") out.sort = sort as SortKey;
    return out;
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(beatsCatalogQuery),
      context.queryClient.ensureQueryData(beatsTiersQuery),
      context.queryClient.ensureQueryData(beatsEngagementQuery),
    ]);
    return null;
  },
  head: ({ match }) => {
    // Filtered permutations are navigational, not canonical documents.
    const filtered = activeFilterCount({ ...EMPTY_FILTERS, ...(match.search as Search) } as BeatFilters) > 0;
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        ...(filtered ? [{ name: "robots", content: "noindex, follow" }] : []),
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl("/beats/catalog") },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: absoluteUrl("/beats/catalog") }],
    };
  },
  component: Catalog,
});

function Catalog() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const beats = useQuery(beatsCatalogQuery);
  const tiers = useQuery(beatsTiersQuery);
  const engagement = useQuery(beatsEngagementQuery);

  const filters: BeatFilters = { ...EMPTY_FILTERS, ...search } as BeatFilters;
  const sort: SortKey = search.sort ?? "newest";

  const rows = beats.data ?? [];
  const facets = useMemo(() => facetsFor(rows), [rows]);
  const results = useMemo(
    () => sortBeats(applyBeatFilters(rows, filters, tiers.data ?? []), sort, tiers.data ?? [], engagement.data ?? []),
    [rows, filters, sort, tiers.data, engagement.data],
  );

  function setFilter(patch: Partial<Search>) {
    const key = Object.keys(patch)[0] ?? "";
    if (key === "q") track("beat_search", { terms: String(patch.q ?? "").slice(0, 60) });
    else if (key === "sort") track("beat_sort", { sort: String(patch.sort) });
    else track("beat_filter", { facet: key, value: String(Object.values(patch)[0] ?? "") });
    void navigate({ search: (prev: Search) => ({ ...prev, ...patch }), replace: true });
  }

  const active = activeFilterCount(filters);

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/beats" className="text-gold hover:text-gold-luminous">
          Beats
        </Link>{" "}
        / Catalog
      </nav>
      <header className="mt-4 max-w-2xl">
        <h1 className="font-serif text-5xl">Catalog.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Tagged previews stream here. Full-quality files are released only with an issued licence.
        </p>
      </header>

      {rows.length > 0 && (
        <form
          role="search"
          aria-label="Search the beat catalog"
          onSubmit={(e) => e.preventDefault()}
          className="mt-10 space-y-4"
        >
          <div className="flex flex-wrap gap-3">
            <label className="sr-only" htmlFor="beat-q">
              Search beats by title, mood, tempo or tag
            </label>
            <input
              id="beat-q"
              type="search"
              value={filters.q}
              onChange={(e) => setFilter({ q: e.target.value })}
              placeholder="Search title, mood, tempo or tag"
              className="min-w-56 flex-1 rounded-full border hairline bg-charcoal px-4 py-2.5 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none"
            />
            <label className="sr-only" htmlFor="beat-sort">
              Sort results
            </label>
            <select
              id="beat-sort"
              value={sort}
              onChange={(e) => setFilter({ sort: e.target.value as SortKey })}
              className="rounded-full border hairline bg-charcoal px-4 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none"
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-3">
            <Facet
              id="genre"
              label="Genre"
              value={filters.genre}
              options={facets.genres}
              onChange={(v) => setFilter({ genre: v })}
            />
            <Facet
              id="mood"
              label="Mood"
              value={filters.mood}
              options={facets.moods}
              onChange={(v) => setFilter({ mood: v })}
            />
            <Facet
              id="key"
              label="Key"
              value={filters.musicalKey}
              options={facets.keys}
              onChange={(v) => setFilter({ musicalKey: v })}
            />
            <Facet
              id="bpm"
              label="Tempo"
              value={filters.bpmBand}
              options={facets.bpmBands.map((b) => b.key)}
              labels={Object.fromEntries(facets.bpmBands.map((b) => [b.key, b.label]))}
              onChange={(v) => setFilter({ bpmBand: v })}
            />
            <Facet
              id="collection"
              label="Collection"
              value={filters.collection}
              options={facets.collections}
              onChange={(v) => setFilter({ collection: v })}
            />
            <Facet
              id="topic"
              label="Topic"
              value={filters.topic}
              options={facets.topics}
              labels={Object.fromEntries(facets.topics.map((t) => [t, getTopic(t)?.name ?? t]))}
              onChange={(v) => setFilter({ topic: v })}
            />
            <label className="flex items-center gap-2 rounded-full border hairline bg-charcoal px-4 py-2.5 text-sm text-ivory">
              <input
                type="checkbox"
                checked={filters.instantOnly}
                onChange={(e) => setFilter({ instantOnly: e.target.checked })}
                className="accent-[hsl(45_80%_60%)]"
              />
              Fixed price available
            </label>
            {active > 0 && (
              <button
                type="button"
                onClick={() =>
                  void navigate({ search: { ...EMPTY_FILTERS, sort } as Search, replace: true })
                }
                className="rounded-full border hairline px-4 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-gold"
              >
                Clear {active} filter{active === 1 ? "" : "s"}
              </button>
            )}
          </div>

          {facets.tags.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label="Popular tags">
              {facets.tags.map((t) => (
                <li key={t}>
                  <button
                    type="button"
                    aria-pressed={filters.tag === t}
                    onClick={() => setFilter({ tag: filters.tag === t ? "" : t })}
                    className={
                      filters.tag === t
                        ? "rounded-full border border-gold px-3 py-1 text-xs text-gold-luminous"
                        : "rounded-full border hairline px-3 py-1 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                    }
                  >
                    {t}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>
      )}

      <p aria-live="polite" className="mt-8 text-xs uppercase tracking-[0.2em] text-gold/80">
        {beats.isLoading
          ? "Loading…"
          : `${results.length} beat${results.length === 1 ? "" : "s"}${active ? " matching your filters" : ""}`}
      </p>

      {beats.isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading the catalog…</p>
      ) : beats.isError ? (
        <p role="alert" className="mt-6 rounded-2xl border hairline bg-charcoal p-6 text-sm text-red-400">
          The catalog could not be loaded. Please refresh and try again.
        </p>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-2xl border hairline bg-charcoal p-12 text-center">
          <p className="font-serif text-3xl text-ivory">No beats released yet.</p>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            The licensing catalog opens with the first release. Custom licensing requests are open now.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/beats/custom"
              className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous transition-all hover:bg-gold hover:text-obsidian"
            >
              Request a custom licence
            </Link>
            <Link
              to="/beats/license"
              className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-gold"
            >
              Read the licence terms
            </Link>
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="mt-6 rounded-2xl border hairline bg-charcoal p-10 text-center">
          <p className="font-serif text-2xl text-ivory">Nothing matches those filters.</p>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            Try widening the tempo range or clearing a facet — or request a custom licence for a sound that
            does not exist in the catalog yet.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => void navigate({ search: { ...EMPTY_FILTERS, sort } as Search, replace: true })}
              className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-gold"
            >
              Clear all filters
            </button>
            <Link
              to="/beats/custom"
              className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
            >
              Request a custom licence
            </Link>
          </div>
        </div>
      ) : (
        <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((beat) => (
            <li key={beat.id}>
              <BeatCard
                beat={beat}
                tiers={tiers.data ?? []}
                onSelect={() => track("beat_card_click", { slug: beat.slug, section: "catalog" })}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Facet({
  id,
  label,
  value,
  options,
  labels,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  labels?: Record<string, string>;
  onChange: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div>
      <label className="sr-only" htmlFor={`facet-${id}`}>
        Filter by {label.toLowerCase()}
      </label>
      <select
        id={`facet-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-full border hairline bg-charcoal px-4 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none"
      >
        <option value="">All {label.toLowerCase()}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {labels?.[o] ?? o}
          </option>
        ))}
      </select>
    </div>
  );
}
